import { Accessibility, ContentItemFragment } from 'app/graphql/__generated__/types/contento';
import Link from 'next/link';
import React from 'react';
import { getContentUrl } from '../../utils/contentoUtils';
import { createImageProxyUrl } from '../../utils/svtimage';
import useTrackImpression, { OnItemImpressionType } from '../../utils/useTrackImpression';
import { PlayBadgeSize } from '../PlayBadge/PlayBadge';
import PlayImage, { ImageRatio } from '../PlayImage/PlayImage';
import Caption from './components/Caption/Caption';
import LiveBadge from './components/LiveBadge/LiveBadge';
import { CardWrapper, StyledShowcaseCard } from './ShowcaseCard.styles';

type Props = {
    index: number;
    item: ContentItemFragment;
    onItemClick: (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        itemPosition: number,
        svtId: string,
        autoPlay?: boolean,
        b?: boolean
    ) => void;
    onItemImpression: OnItemImpressionType;
    currentAccessibilityChoiceContento?: Accessibility;
    big?: boolean;
    twoColumns?: boolean;
    badgeSize?: PlayBadgeSize;
    srcSet?: string;
    className?: string;
};

const ShowcaseCard = ({
    index,
    item,
    onItemClick,
    onItemImpression,
    currentAccessibilityChoiceContento = Accessibility.Default,
    big,
    twoColumns,
    badgeSize = PlayBadgeSize.LARGE,
    srcSet,
    className,
}: Props) => {
    const [trackingRef] = useTrackImpression(onItemImpression, index + 1, item.item.svtId);

    const imageUrl = createImageProxyUrl(item?.images?.wide?.id, item?.images?.wide?.changed, 'wide', 800);

    const imageAltText = item?.images?.wide?.description || '';

    const url = getContentUrl(item.item, currentAccessibilityChoiceContento);

    return (
        <StyledShowcaseCard
            key={item.item.svtId}
            data-rt="display-window"
            className={className}
            big={big}
            twoColumns={twoColumns}
            ref={trackingRef}
        >
            <Link href={url} passHref>
                <CardWrapper
                    onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
                        onItemClick(e, index + 1, item.item.svtId)
                    }
                    className={`lp_pos${index + 1}`}
                >
                    {item.badge && <LiveBadge badge={item.badge} size={badgeSize} />}
                    <PlayImage
                        imageRatio={ImageRatio.SIXTEEN_BY_NINE}
                        ssr
                        src={imageUrl}
                        srcSet={srcSet}
                        alt={imageAltText}
                    />
                    <Caption byline={item.byline} subHeading={item.subHeading} heading={item.heading} />
                </CardWrapper>
            </Link>
        </StyledShowcaseCard>
    );
};

export default React.memo(ShowcaseCard);
